import { apiFetch } from "../../../shared/apiClient";
import { isAbortError } from "../../../shared/utils/abortError";

const CREATE_LINKS_ROUTE = "/api/ai/preslink/create-presentation-links";
/** The relay gives up after 90 s, so its typed 504 arrives before this client timeout. */
const DEFAULT_TIMEOUT_MS = 100_000;
const MAX_REQUEST_BYTES = 50 * 1024 * 1024;
const MAX_RESPONSE_TEXT_BYTES = 96 * 1024 * 1024;
const TIMESTAMP_PATTERN = /^\d{14}$/;

/** The relay's own error codes. Response text is never reused, because it can name local folders. */
const SERVER_ERROR_MESSAGES = new Map<string, string>([
  ["preslink_create_links_api_unconfigured", "PresLink slide linking is not configured on this server."],
  ["preslink_create_links_invalid_request", "PresLink slide request is invalid."],
  ["preslink_create_links_request_too_large", "PresLink slide request exceeds the supported size limit."],
  ["preslink_create_links_auth_unavailable", "PresLink slide linking needs a signed-in Finster session."],
  ["preslink_create_links_response_too_large", "Smart Slide's response exceeded the supported size limit."],
  ["preslink_create_links_api_timeout", "Smart Slide timed out."],
  ["preslink_create_links_api_unavailable", "Smart Slide is unavailable right now."],
  ["preslink_create_links_failed", "Smart Slide could not link the PresLink slides."],
]);

export interface PreslinkCreateLinksFile {
  fileName: string;
  /** Raw file bytes as base64. */
  base64: string;
}

export interface PreslinkCreateLinksRequest {
  /** The deck's folder; Smart Slide writes it into every tag's Path. */
  folderPath: string;
  presentations: PreslinkCreateLinksFile[];
  excelFiles: PreslinkCreateLinksFile[];
}

export interface PreslinkLinkedPresentation extends PreslinkCreateLinksFile {
  shapesUpdated: number;
}

export interface PreslinkCreateLinksResponse {
  /** UTC `YYYYMMDDHHMMSS`, appended to every returned workbook name. */
  timestamp: string;
  /** In request order. */
  presentations: PreslinkLinkedPresentation[];
  /** In request order, renamed `<stem>_<timestamp><ext>`. */
  excelFiles: PreslinkCreateLinksFile[];
}

export interface PreslinkCreateLinksOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class PreslinkCreateLinksError extends Error {
  readonly code: string;
  readonly status?: number;

  constructor(code: string, message: string, status?: number) {
    super(message);
    this.name = "PreslinkCreateLinksError";
    this.code = code;
    this.status = status;
    Object.setPrototypeOf(this, PreslinkCreateLinksError.prototype);
  }
}

function serverError(code: string, status?: number): PreslinkCreateLinksError {
  return new PreslinkCreateLinksError(
    code,
    SERVER_ERROR_MESSAGES.get(code) ?? "Smart Slide could not link the PresLink slides.",
    status,
  );
}

function invalidResponse(message: string): PreslinkCreateLinksError {
  return new PreslinkCreateLinksError("preslink_create_links_invalid_response", message);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function readReturnedFile(entry: unknown): PreslinkCreateLinksFile & { record: Record<string, unknown> } {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
    throw invalidResponse("Smart Slide returned an invalid file entry.");
  }
  const record = entry as Record<string, unknown>;
  if (!isNonEmptyString(record.file_name) || !isNonEmptyString(record.zip_base64)) {
    throw invalidResponse("Smart Slide returned a file without a name or contents.");
  }
  return { fileName: record.file_name, base64: record.zip_base64, record };
}

function parseCreateLinksResponse(
  payload: unknown,
  request: PreslinkCreateLinksRequest,
): PreslinkCreateLinksResponse {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw invalidResponse("Smart Slide returned an invalid response.");
  }
  const object = payload as Record<string, unknown>;
  if (typeof object.timestamp !== "string" || !TIMESTAMP_PATTERN.test(object.timestamp)) {
    throw invalidResponse("Smart Slide returned an invalid timestamp.");
  }
  if (
    !Array.isArray(object.presentations) ||
    object.presentations.length !== request.presentations.length
  ) {
    throw invalidResponse("Smart Slide returned a different number of slides than requested.");
  }
  if (!Array.isArray(object.excel_files) || object.excel_files.length !== request.excelFiles.length) {
    throw invalidResponse("Smart Slide returned a different number of workbooks than requested.");
  }
  const presentations = object.presentations.map((entry) => {
    const { fileName, base64, record } = readReturnedFile(entry);
    const shapesUpdated = record.shapes_updated;
    if (typeof shapesUpdated !== "number" || !Number.isInteger(shapesUpdated) || shapesUpdated < 0) {
      throw invalidResponse("Smart Slide returned an invalid linked shape count.");
    }
    return { fileName, base64, shapesUpdated };
  });
  const excelFiles = object.excel_files.map((entry) => {
    const { fileName, base64 } = readReturnedFile(entry);
    return { fileName, base64 };
  });
  return { timestamp: object.timestamp, presentations, excelFiles };
}

/**
 * Ask Smart Slide to point library slides' PresLink tags at the deck's folder and to
 * timestamp their workbook names. Files are sent and returned whole, as base64.
 */
export async function createPresentationLinks(
  request: PreslinkCreateLinksRequest,
  options: PreslinkCreateLinksOptions = {},
): Promise<PreslinkCreateLinksResponse> {
  if (!request.folderPath || request.presentations.length === 0) {
    throw serverError("preslink_create_links_invalid_request");
  }
  const toWire = (file: PreslinkCreateLinksFile) => ({
    file_name: file.fileName,
    zip_base64: file.base64,
  });
  const body = JSON.stringify({
    path: request.folderPath,
    presentations: request.presentations.map(toWire),
    excel_files: request.excelFiles.map(toWire),
  });
  if (new TextEncoder().encode(body).byteLength > MAX_REQUEST_BYTES) {
    throw serverError("preslink_create_links_request_too_large");
  }

  const controller = new AbortController();
  const onAbort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) controller.abort(options.signal.reason);
  else options.signal?.addEventListener("abort", onAbort, { once: true });
  let timedOut = false;
  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    const response = await apiFetch(CREATE_LINKS_ROUTE, {
      method: "POST",
      cache: "no-store",
      headers: { "Content-Type": "application/json" },
      body,
      signal: controller.signal,
    });
    const declaredLength = Number(response.headers.get("content-length"));
    if (Number.isFinite(declaredLength) && declaredLength > MAX_RESPONSE_TEXT_BYTES) {
      throw serverError("preslink_create_links_response_too_large", response.status);
    }
    const text = await response.text();
    if (new TextEncoder().encode(text).byteLength > MAX_RESPONSE_TEXT_BYTES) {
      throw serverError("preslink_create_links_response_too_large", response.status);
    }
    let payload: unknown;
    try {
      payload = text ? JSON.parse(text) : undefined;
    } catch {
      if (response.ok) throw invalidResponse("Smart Slide returned invalid JSON.");
    }
    if (!response.ok) {
      const code =
        payload && typeof payload === "object"
          ? (payload as Record<string, unknown>).error
          : undefined;
      if (typeof code === "string" && SERVER_ERROR_MESSAGES.has(code)) {
        throw serverError(code, response.status);
      }
      throw new PreslinkCreateLinksError(
        "preslink_create_links_failed",
        `Smart Slide request failed (${response.status}).`,
        response.status,
      );
    }
    return parseCreateLinksResponse(payload, request);
  } catch (error) {
    if (isAbortError(error) && timedOut) {
      throw new PreslinkCreateLinksError("preslink_create_links_timeout", "Smart Slide timed out.");
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    options.signal?.removeEventListener("abort", onAbort);
  }
}
